import { Injectable, Injector, inject } from '@angular/core';
import type { Analytics } from '@angular/fire/analytics';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { ENV } from 'src/environment.provider';
import { ApiService } from './api.services';
import { CookieConsentService } from './cookie-consent.service';
import { CLARITY_API } from './clarity-api';
import { FIREBASE_ANALYTICS_API } from './firebase-analytics-api';

@Injectable({
	providedIn: 'root',
})
export class AnalyticsTrackingService {
	private readonly consent = inject(CookieConsentService);
	private readonly firebaseAnalytics = inject(FIREBASE_ANALYTICS_API);
	private readonly clarity = inject(CLARITY_API);
	private readonly env = inject(ENV);
	private readonly injector = inject(Injector);
	private analytics: Analytics | null = null;
	private clarityStarted = false;
	private clarityPending = false;
	private clarityFlag: Promise<boolean> | null = null;

	constructor() {
		this.setConsentMode(false);
		this.syncFromConsent();
	}

	syncFromConsent(): void {
		if (this.consent.isAccepted()) {
			this.enableAnalytics();
			return;
		}
		this.setConsentMode(false);
		this.syncClarity();
	}

	logEvent(eventName: string, eventParams?: { [key: string]: unknown }): void {
		if (!this.consent.isAccepted() || !this.analytics) {
			return;
		}
		this.firebaseAnalytics.logEvent(this.analytics, eventName, eventParams);
	}

	private enableAnalytics(): void {
		this.setConsentMode(true);
		this.analytics ??= this.firebaseAnalytics.getAnalytics();
		this.syncClarity();
	}

	private syncClarity(): void {
		if (this.clarityStarted || this.clarityPending) {
			return;
		}
		if (!this.consent.isAccepted()) {
			return;
		}
		const projectId = this.env.clarityProjectId?.trim();
		if (!projectId) {
			return;
		}
		this.clarityPending = true;
		// Defer so ApiService can finish constructing. It injects this service.
		void Promise.resolve().then(() => this.startClarity(projectId));
	}

	private async startClarity(projectId: string): Promise<void> {
		try {
			const enabled = await this.isClarityFlagOn();
			if (!enabled || !this.consent.isAccepted() || this.clarityStarted) {
				this.clarityPending = false;
				return;
			}
			this.clarity.init(projectId);
			this.clarity.consentV2({
				ad_Storage: 'denied',
				analytics_Storage: 'granted',
			});
			this.clarityStarted = true;
		} catch {
			this.clarityPending = false;
		}
	}

	private isClarityFlagOn(): Promise<boolean> {
		this.clarityFlag ??= this.injector
			.get(ApiService)
			.getFeatureFlagByName(FeatureFlagName.enableClarityTracking)
			.then((flag) => flag?.value === true)
			.catch(() => false);
		return this.clarityFlag;
	}

	private setConsentMode(granted: boolean): void {
		const gtag = (window as Window & { gtag?: (...args: unknown[]) => void })
			.gtag;
		if (typeof gtag !== 'function') {
			return;
		}
		gtag('consent', 'update', {
			analytics_storage: granted ? 'granted' : 'denied',
			ad_storage: 'denied',
			ad_user_data: 'denied',
			ad_personalization: 'denied',
		});
	}
}
